import React, { useEffect } from 'react';
import JobFormPage from '../JobFormPage';
import JobTitleInput from '../HeadlineDescriptionPage/JobTitleInput';
import JobDescriptionInput from '../HeadlineDescriptionPage/JobDescriptionInput';
import { FieldStates } from '../../FormTypes';
import OneTimeJobDateInput from './OneTimeJobDateInput';
import moment from 'moment';
import OneTimeJobTimeInput from './OneTimeJobTimeInput';
import OneTimePayRateInput from './OneTimePayRateInput';
import { IPayFormFields, JobTimes, ProviderSelection } from 'parent-portal/forms/JobRequestTypes';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import { useMediaQuery } from '@material-ui/core';
import theme from 'theme';
import MoreOptions from './MoreOptions';
import KidSelectionInput from '../../KidSelectionInput';
import { Kid } from 'models/Kid';
import { useUserContext } from 'UserContext';
import { useKidPetContext } from 'parent-portal/forms/contexts/KidPetContext';
import PreferredWorkersInput from '../ProviderPreferencesPage/PreferredWorkersInput';
import PastWorkersInput from '../ProviderPreferencesPage/PastWorkersInput';
import { styled } from '@mui/material';

interface OneTimeJobDetailsPageFields {
    headline: string;
    comments: string;
    jobDate: moment.Moment | null;
    jobTimes: JobTimes;
    pay: IPayFormFields;
    jobTypeId: number;
    kids: Kid[];
    preferredWorkers: ProviderSelection[];
    pastWorkers: ProviderSelection[];
}

interface IProps {
    fieldStates: FieldStates<OneTimeJobDetailsPageFields>;
}

export default function OneTimeJobDetailsPage({
    fieldStates: { kids, jobTypeId, headline, comments, jobDate, jobTimes, pay, preferredWorkers, pastWorkers },
}: IProps) {
    const { user } = useUserContext();
    const { postFamilyKids } = useKidPetContext();
    const { submit, kidChoices, familyHasKids, familyHasPets, jobTypes } = useJobFormContext();
    const sm = useMediaQuery(theme.breakpoints.down('sm'));

    async function onNextClicked() {
        if (user?.family[0].id) {
            await postFamilyKids(user.family[0].id);
        }

        submit();
    }

    function pageIsValid() {
        return (
            headline.isValid && comments.isValid && jobDate.isValid && jobTimes.isValid && pay.isValid && kids.isValid
        );
    }

    useEffect(() => {
        const { isChildCareJob, isPetCareJob } = getJobTypes(jobTypeId.value);
        const jobTypeHasDependents = isChildCareJob || isPetCareJob;

        if (jobTypeHasDependents && kidChoices.length > 0) {
            const dependentType = isChildCareJob ? 'CHILD' : 'PET';
            const newKidsValue = kidChoices.filter((kid) => kid.type === dependentType);
            kids.setValue(newKidsValue);
            kids.setValid(newKidsValue.length > 0);
        } else if (!jobTypeHasDependents) {
            kids.setValid(true);
            kids.setValue([]);
        }
    }, [jobTypeId.value]);

    function getJobTypes(jobTypeId: number) {
        const jobTypeName = jobTypes.find((type) => type.id === jobTypeId)?.name ?? 'other';
        return {
            isChildCareJob: jobTypeName === 'Child Care',
            isPetCareJob: jobTypeName === 'Pet Care',
        };
    }

    const { isChildCareJob, isPetCareJob } = getJobTypes(jobTypeId.value);
    const showKidsInput = (!familyHasKids && isChildCareJob) || (!familyHasPets && isPetCareJob);

    return (
        <JobFormPage onNextClicked={onNextClicked} nextText="Finish" nextDisabled={!pageIsValid()}>
            <JobTitleInput title={headline} />
            <Spacer small={sm} />
            <JobDescriptionInput description={comments} />
            <Spacer small={sm} />
            {showKidsInput ? (
                <>
                    <KidSelectionInput kids={kids} />
                    <Spacer small={sm} />
                </>
            ) : null}
            <OneTimeJobDateInput jobDate={jobDate} />
            <Spacer small={sm} />
            <OneTimeJobTimeInput jobTimes={jobTimes} jobDate={jobDate.value} />
            <Spacer small={sm} />
            <OneTimePayRateInput pay={pay} />
            <Spacer small={sm} />
            <PreferredWorkersInput selected={preferredWorkers.value} onChange={preferredWorkers.setValue} />
            <Spacer small={sm} />
            <PastWorkersInput selected={pastWorkers.value} onChange={pastWorkers.setValue} />
            <Spacer small={sm} />
            <MoreOptions />
        </JobFormPage>
    );
}

const Spacer = styled('div')(({ small }: { small: boolean }) => ({
    height: small ? 0 : 30,
}));
